(() => {
  const debounce = (fn, waitMs) => {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => fn(...args), waitMs);
    };
  };

  const fetchPredictiveSearchHtml = async (query, signal) => {
    const base = window.routes?.predictive_search_url || '/search/suggest';
    const url = new URL(base, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', 'predictive-search');

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { Accept: 'text/html' },
      signal,
    });

    if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
    return response.text();
  };

  const parseProductsFromPredictiveHtml = (htmlText) => {
    const doc = new DOMParser().parseFromString(htmlText, 'text/html');
    const list = doc.querySelector('#predictive-search-results-products-list');
    if (!list) return [];

    const products = [];
    list.querySelectorAll('a.predictive-search__item--link-with-thumbnail').forEach((a) => {
      const title =
        a.querySelector('.predictive-search__item-heading')?.textContent?.trim() ||
        a.textContent?.trim() ||
        '';
      const url = a.getAttribute('href') || '';
      const img = a.querySelector('img.predictive-search__image');
      const imageUrl = img?.getAttribute('src') || '';
      const imageAlt = img?.getAttribute('alt') || '';

      if (!title || !url) return;
      products.push({
        id: url,
        title,
        url,
        imageUrl,
        imageAlt,
      });
    });

    return products;
  };

  const updateSelectedField = (selectedField, selected) => {
    const lines = selected.map((p) => `${p.title} — ${p.url}`);
    selectedField.value = lines.join('\n');
  };

  const renderSelected = (selectedList, selectedWrap, selectedField, selected) => {
    selectedList.innerHTML = '';
    selected.forEach((product) => {
      const li = document.createElement('li');
      li.className = 'custom-order__selected-item';
      li.dataset.productId = product.id;

      const link = document.createElement('a');
      link.className = 'link link--text';
      link.href = product.url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = product.title;

      if (product.imageUrl) {
        const thumb = document.createElement('img');
        thumb.className = 'custom-order__thumb';
        thumb.src = product.imageUrl;
        thumb.alt = product.imageAlt || product.title;
        thumb.loading = 'lazy';
        thumb.width = 52;
        thumb.height = 52;
        li.append(thumb);
      }

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'custom-order__remove button button--tertiary';
      remove.textContent = 'Remove';
      remove.addEventListener('click', () => {
        const idx = selected.findIndex((p) => p.id === product.id);
        if (idx >= 0) selected.splice(idx, 1);
        updateSelectedField(selectedField, selected);
        renderSelected(selectedList, selectedWrap, selectedField, selected);
      });

      const textWrap = document.createElement('div');
      textWrap.className = 'custom-order__selected-text';
      textWrap.append(link);

      li.append(textWrap, remove);
      selectedList.append(li);
    });

    selectedWrap.hidden = selected.length === 0;
  };

  const renderResults = (resultsWrap, products, selected, onAdd) => {
    resultsWrap.innerHTML = '';
    if (!products.length) {
      const empty = document.createElement('p');
      empty.className = 'custom-order__empty caption-large';
      empty.textContent = 'No matching products — try a different search.';
      resultsWrap.append(empty);
      return;
    }

    const ul = document.createElement('ul');
    ul.className = 'custom-order__results list-unstyled';

    products.forEach((product) => {
      const li = document.createElement('li');
      li.className = 'custom-order__result';

      if (product.imageUrl) {
        const img = document.createElement('img');
        img.className = 'custom-order__thumb';
        img.src = product.imageUrl;
        img.alt = product.imageAlt || product.title;
        img.loading = 'lazy';
        img.width = 56;
        img.height = 56;
        li.append(img);
      }

      const title = document.createElement('a');
      title.className = 'custom-order__result-title link link--text';
      title.href = product.url;
      title.target = '_blank';
      title.rel = 'noopener';
      title.textContent = product.title;

      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'custom-order__add button button--secondary';
      const alreadySelected = selected.some((p) => p.id === product.id);
      add.textContent = alreadySelected ? 'Added' : 'Add';
      if (alreadySelected) {
        add.disabled = true;
      } else {
        add.addEventListener('click', () => onAdd(product));
      }

      const mid = document.createElement('div');
      mid.className = 'custom-order__result-mid';
      mid.append(title);

      li.append(mid, add);
      ul.append(li);
    });

    resultsWrap.append(ul);
  };

  const init = (root) => {
    const form = root.closest('form');
    const dialog = root.closest('.custom-order')?.querySelector('[data-product-picker-dialog]');
    const openBtn = root.querySelector('[data-product-picker-open]');
    const closeBtn = dialog?.querySelector('[data-product-picker-close]');

    const input = dialog?.querySelector('[data-product-search-input]');
    const resultsWrap = dialog?.querySelector('[data-product-search-results]');
    const selectedWrap = root.querySelector('[data-selected-wrap]');
    const selectedList = root.querySelector('[data-selected-list]');
    const selectedField = root.querySelector('[data-selected-field]');

    if (
      !form ||
      !dialog ||
      !openBtn ||
      !input ||
      !resultsWrap ||
      !selectedWrap ||
      !selectedList ||
      !selectedField
    )
      return;

    const stepsWrap = root.querySelector('[data-co-steps]');
    const steps = stepsWrap ? Array.from(stepsWrap.querySelectorAll('[data-co-step]')) : [];
    const nextButtons = stepsWrap ? Array.from(stepsWrap.querySelectorAll('[data-co-next]')) : [];
    const progress = root.querySelector('[data-co-progress]');
    const progressBar = root.querySelector('[data-co-progress-bar]');
    const progressLabel = root.querySelector('[data-co-progress-label]');
    const detailsField = root.querySelector('[data-co-details]');
    const countEl = root.querySelector('[data-co-count]');
    const requestCall = root.querySelector('[data-request-call]');
    const callFields = root.querySelector('[data-call-fields]');

    const selected = [];
    let abortController = null;

    const getActiveStepIndex = () => steps.findIndex((s) => s.hasAttribute('open'));

    const openStep = (index) => {
      steps.forEach((s, i) => {
        if (i === index) s.setAttribute('open', '');
        else s.removeAttribute('open');
      });
      updateProgress();
      steps[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const isFieldValid = (field) => {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement))
        return true;
      if (field.disabled) return true;
      return field.checkValidity();
    };

    const markValidity = (field) => {
      if (!field || !(field instanceof HTMLElement)) return;
      if (isFieldValid(field)) {
        field.removeAttribute('aria-invalid');
        field.classList.remove('custom-order__invalid');
      } else {
        field.setAttribute('aria-invalid', 'true');
        field.classList.add('custom-order__invalid');
      }
    };

    const validateStep = (stepEl) => {
      const fields = Array.from(stepEl.querySelectorAll('input, textarea, select'));
      const required = fields.filter((f) => f.hasAttribute('required'));
      let ok = true;
      required.forEach((f) => {
        markValidity(f);
        if (!isFieldValid(f)) ok = false;
      });
      if (!ok) {
        const firstBad = required.find((f) => !isFieldValid(f));
        firstBad?.focus?.();
      }
      return ok;
    };

    const updateProgress = () => {
      if (!progress || !progressBar || !progressLabel || steps.length === 0) return;
      const idx = Math.max(0, getActiveStepIndex());
      const total = steps.length;
      const pct = ((idx + 1) / total) * 100;
      progressBar.style.width = `${pct}%`;
      progressLabel.textContent = `Step ${idx + 1} of ${total}`;
    };

    const updateCount = () => {
      if (!detailsField || !countEl) return;
      const max = 1000;
      const len = (detailsField.value || '').length;
      countEl.textContent = `${Math.min(len, max)} / ${max}`;
    };

    const onAdd = (product) => {
      if (selected.some((p) => p.id === product.id)) return;
      selected.push(product);
      updateSelectedField(selectedField, selected);
      renderSelected(selectedList, selectedWrap, selectedField, selected);
      // Keep the dialog open so multiple products can be added quickly.
      resultsWrap.innerHTML = '';
      input.value = '';
      input.focus();
    };

    const runSearch = debounce(async () => {
      const q = (input.value || '').trim();
      if (q.length < 2) {
        resultsWrap.innerHTML = '';
        return;
      }

      if (abortController) abortController.abort();
      abortController = new AbortController();

      try {
        const htmlText = await fetchPredictiveSearchHtml(q, abortController.signal);
        const products = parseProductsFromPredictiveHtml(htmlText);
        renderResults(resultsWrap, products, selected, onAdd);
      } catch (e) {
        if (e?.name === 'AbortError') return;
        resultsWrap.innerHTML = '';
      }
    }, 250);

    input.addEventListener('input', runSearch);

    openBtn.addEventListener('click', () => {
      try {
        dialog.showModal();
      } catch (e) {
        dialog.setAttribute('open', '');
      }
      resultsWrap.innerHTML = '';
      input.value = '';
      window.setTimeout(() => input.focus(), 10);
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        try {
          dialog.close();
        } catch (e) {
          dialog.removeAttribute('open');
        }
      });
    }

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    if (detailsField) {
      updateCount();
      detailsField.addEventListener('input', updateCount);
    }

    if (requestCall && callFields) {
      const syncCallFields = () => {
        callFields.hidden = !requestCall.checked;
        callFields.querySelectorAll('input').forEach((el) => {
          el.disabled = !requestCall.checked;
        });
      };
      syncCallFields();
      requestCall.addEventListener('change', syncCallFields);
    }

    if (steps.length) {
      updateProgress();

      steps.forEach((stepEl) => {
        stepEl.addEventListener('toggle', () => {
          if (stepEl.open) updateProgress();
        });
      });

      nextButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const currentStep = btn.closest('[data-co-step]');
          const idx = currentStep ? steps.indexOf(currentStep) : -1;
          if (idx < 0) return;
          if (!validateStep(currentStep)) return;
          openStep(Math.min(idx + 1, steps.length - 1));
        });
      });

      form.addEventListener('submit', (e) => {
        // Validate all required fields in visible steps before submit.
        const requiredFields = Array.from(form.querySelectorAll('[required]'));
        let ok = true;
        requiredFields.forEach((f) => {
          markValidity(f);
          if (!isFieldValid(f)) ok = false;
        });
        if (!ok) {
          e.preventDefault();
          const firstBad = requiredFields.find((f) => !isFieldValid(f));
          // Open the step containing the first invalid field
          const stepEl = firstBad?.closest?.('[data-co-step]');
          if (stepEl) openStep(Math.max(0, steps.indexOf(stepEl)));
          firstBad?.focus?.();
        }
      });
    }
  };

  window.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-custom-order-form]').forEach((el) => init(el));
  });
})();
